import { FC, ReactNode, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SearchContextProvider, useSearchContext } from "../../../../../context/searchContext";
import useI18n from "../../../../../hooks/useTranslations";
import { CustomError } from "../../../../../http/errors";
import { useRoofCategoriesApi } from "../../../../../http/patrol/useRoofCategories";
import FlexContent from "../../../../ui/FlexContent";
import Header, { HeaderPath } from "../../../../ui/Header";
import Tabs, { Tab } from "../../../../ui/Tabs";
import { PATROL } from "../../AdminSystemSettings";
import RoofCategoryDetails from "./RoofCategoryDetails";
import RoofTypes from "./roofTypes/RoofTypes";

const RoofCategory: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { categoryId } = useParams<{ categoryId: string }>();
  const roofCategoriesApi = useRoofCategoriesApi();
  const [searchParams] = useSearchParams();
  const selectedIndex = Number(searchParams.get("l_tab")) || 0;

  const aborter = useRef(new AbortController());
  const { error, data, isMutating, trigger } = roofCategoriesApi.getAdminRoofCategory(aborter.current.signal);

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: PATROL },
    {
      url: `/admin/systems/${systemId}/settings?tab=2`,
      display: `${i18n.translation.common.settings}: ${i18n.translation.common.roofs}`,
    },
    { display: data?.name ?? "" },
  ];

  useEffect(() => {
    categoryId && trigger(categoryId);
  }, [categoryId]);

  const tabs: Tab[] = [
    { title: i18n.translation.common.generalData, hasSubTabs: true },
    { title: i18n.translation.roofTypes.plural },
  ];

  const contents: ReactNode[] = [
    <Tabs.SubTab>
      <FlexContent padding direction="row">
        <RoofCategoryDetails roofCategory={!error ? data : undefined} disabled={isMutating || !!error} />
      </FlexContent>
    </Tabs.SubTab>,
    <RoofTypes active={selectedIndex === 1} />,
  ];

  return (
    <SearchContextProvider>
      <RoofCategoryContent
        title={`${i18n.translation.roofCategories.singular}: ${data?.name ?? ""}`}
        error={error}
        path={path}
        tabs={tabs}
        contents={contents}
      />
    </SearchContextProvider>
  );
};

export default RoofCategory;

type RoofCategoryContentProps = {
  title: string;
  error?: CustomError;
  path: HeaderPath[];
  tabs: Tab[];
  contents: ReactNode[];
};

const RoofCategoryContent: FC<RoofCategoryContentProps> = ({ title, error, path, tabs, contents }) => {
  const { actions, title: tabTitle } = useSearchContext();

  return (
    <>
      <Header title={tabTitle ? `${title} · ${tabTitle}` : title} error={error} path={path} buttons={actions} />
      <Tabs urlParameter="l_tab" tabs={tabs} contents={contents} />
    </>
  );
};
